import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Icon } from '../../components/Icon'
import { coversTabBar } from './sections'
import { useBack } from './useBack'

/**
 * A screen's header. A section (Search, Settings, Administration) is reached from the tab bar, which stays on it, so on a
 * phone it has a large title and no back arrow, as an iPhone's tab roots do; a screen inside a conversation covers the
 * bar and keeps its way back.
 */
interface Back {
  to: string
  label: string
  /** A computer moves between Administration's sections by its tab strip, so the arrow is the phone's. */
  phoneOnly?: boolean
}

export function ScreenHeader({ title, subtitle, actions, back: backTo }: { title: string; subtitle?: ReactNode; actions?: ReactNode; back?: Back }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const back = useBack()
  // A section reached from the tab bar has no back arrow; a screen inside one (Administration's) leads back to it.
  const section = !coversTabBar(pathname) && !backTo
  const ref = useRef<HTMLElement>(null)
  // The page scrolls under the title: a line under the title once it has, none at the top.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const body = ref.current?.parentElement?.querySelector<HTMLElement>(':scope > .screen-body')
    if (!body) return
    const update = () => setScrolled(body.scrollTop > 0)
    update()
    body.addEventListener('scroll', update, { passive: true })
    return () => body.removeEventListener('scroll', update)
  }, [])
  const classes = ['chat-header', section ? 'section-header' : '', scrolled ? 'is-scrolled' : ''].filter(Boolean).join(' ')
  return (
    <header ref={ref} className={classes}>
      {section ? null : (
        <button
          type="button"
          className={backTo?.phoneOnly ? 'icon-button back-button on-phone-only' : 'icon-button back-button'}
          aria-label={backTo ? backTo.label : back.label}
          onClick={backTo ? () => navigate(backTo.to) : back.go}
        >
          <Icon name="back" />
        </button>
      )}
      <div className="chat-heading">
        <h2>{title}</h2>
        {subtitle ? <p className="chat-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="chat-actions">{actions}</div> : null}
    </header>
  )
}
